import { Avatar, Box, Flex, Icon, Text } from "@daangn/carotene";
import {
  IconArrowDownHorizlineLine,
  IconBracketLeftArrowRightLine,
  IconCheckmarkShieldFill,
  IconDot3HorizontalChatbubbleLeftLine,
  IconShoppingbagLine,
} from "@daangn/react-monochrome-icon";
import { IconReceiptLine } from "@daangn/react-monochrome-icon";
import type { KarrotLocalCountryCode } from "@karrotmarket-com/core";
import { isNil } from "lodash";
import { REDIRECT_PATH_QUERY_KEY } from "shared/redirectPathQueryKey";
import {
  DropdownMenuContent,
  type DropdownMenuContentProps,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "~/design-system/components/DropdownMenu/DropdownMenu";
import { Link } from "~/remix-lib";
import { useNormalizeHref } from "~/utils/useNormalizeHref";

type UserDropdownContentProps = {
  user: {
    id: string;
    nickname: string;
    profileImageUrl: string;
    isPersonaVerified: boolean;
    userLocations: {
      h3Index: number;
      name: string;
      current: boolean;
      checkinCount: number;
      rangeLevel: number;
    }[];
  };
  karrotLocalCountryCode: KarrotLocalCountryCode;
  currentUserLocationName: string | undefined;
  handleDownloadApp: () => void;
} & DropdownMenuContentProps;

export function UserDropdownContent({
  user,
  karrotLocalCountryCode,
  handleDownloadApp,
  currentUserLocationName,
  ...props
}: UserDropdownContentProps) {
  const { makeHref } = useNormalizeHref();

  // FIXME: use permalink
  const userPath = `/${karrotLocalCountryCode}/users/${user.id}`;

  const handleSignOut = () => {
    window.location.href = `/ca/logout?${REDIRECT_PATH_QUERY_KEY}=${encodeURIComponent(
      makeHref({
        pathname: location.pathname,
        queryString: location.search,
      }),
    )}`;
  };

  return (
    <DropdownMenuContent {...props} style={{ width: "200px" }}>
      <Flex alignItems="center" paddingTop={0.5} paddingBottom={2} paddingX={2}>
        <Text size="xxsmall" asChild color="neutralSubtle">
          <Box fontWeight="bold">Signed in as</Box>
        </Text>
      </Flex>

      <Flex alignItems="center" gap={2} paddingX={2} paddingY={1.5}>
        <Avatar size={36} src={user.profileImageUrl} alt="" />
        <Flex flexDirection="column" flexGrow={1}>
          <Flex alignItems="center" gap={1}>
            <Text weight="strong" size="small">
              {user.nickname}
            </Text>
            {user.isPersonaVerified && (
              <Icon
                svg={
                  <IconCheckmarkShieldFill
                    aria-label={`${user.nickname} is verified`}
                    size={14}
                  />
                }
              />
            )}
          </Flex>
          {!isNil(currentUserLocationName) && (
            <Text size="xxsmall" color="neutralSubtle">
              {currentUserLocationName}
            </Text>
          )}
        </Flex>
      </Flex>

      <DropdownMenuSeparator />

      <Text size="xxsmall" color="neutralSubtle" asChild>
        <Box paddingX={2} paddingY={1} fontWeight="bold">
          My Karrot
        </Box>
      </Text>

      <DropdownMenuItem>
        <Link href={`${userPath}/`}>
          <Flex alignItems="center" gap={2}>
            <Box padding={1}>
              <Icon aria-hidden size={4} svg={<IconReceiptLine />} />
            </Box>
            <Text size="small">Listings</Text>
          </Flex>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem>
        <Link href={`${userPath}/reviews`}>
          <Flex alignItems="center" gap={2}>
            <Box padding={1}>
              <Icon
                aria-hidden
                size={4}
                svg={<IconDot3HorizontalChatbubbleLeftLine />}
              />
            </Box>
            <Text size="small">Reviews</Text>
          </Flex>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem>
        <Link href={`${userPath}/purchases`}>
          <Flex alignItems="center" gap={2}>
            <Box padding={1}>
              <Icon aria-hidden size={4} svg={<IconShoppingbagLine />} />
            </Box>
            <Text size="small">Purchases</Text>
          </Flex>
        </Link>
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuItem onClick={handleDownloadApp}>
        <Flex alignItems="center" gap={2}>
          <Box padding={1}>
            <Icon aria-hidden size={4} svg={<IconArrowDownHorizlineLine />} />
          </Box>
          <Text size="small">Download the app</Text>
        </Flex>
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={handleSignOut}>
        <Flex alignItems="center" gap={2}>
          <Box padding={1}>
            <Icon
              aria-hidden
              size={4}
              svg={<IconBracketLeftArrowRightLine />}
            />
          </Box>
          <Text size="small">Sign out</Text>
        </Flex>
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}

export function getCurrentCheckInLocationName(
  userLocations: {
    name: string;
    current: boolean;
  }[],
): string | undefined {
  return userLocations.find((location) => location.current)?.name;
}
